import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, isApiError } from './client';
import { InvoiceDetailResponse } from './types';

export interface RestoreInvoiceParams {
  publicToken: string;
  historyToken: string;
}

/**
 * Hook to restore an invoice to a historical version.
 * Calls POST /api/v2/invoices/{id}/restore/{historyToken}/
 */
export function useRestoreInvoice() {
  const queryClient = useQueryClient();

  return useMutation<InvoiceDetailResponse, Error, RestoreInvoiceParams>({
    mutationFn: async ({ publicToken, historyToken }) => {
      try {
        const response = await api.post<InvoiceDetailResponse>(
          `/v2/invoices/${publicToken}/restore/${historyToken}/`
        );
        return response.data;
      } catch (err: unknown) {
        if (isApiError(err)) {
          throw new Error(err.error.message || 'Failed to restore invoice version.');
        }
        throw err;
      }
    },
    onSuccess: (data, { publicToken }) => {
      // Invalidate queries so UI reflects restored version immediately
      queryClient.setQueryData(['invoice', publicToken, undefined], data);
      queryClient.invalidateQueries({ queryKey: ['invoice', publicToken] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-kpis'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-charts'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-360'] });
    },
  });
}
